import type {
  InviteMemberInput,
  Rate,
  RateInput,
  RateScope,
  RecalculateInput,
  RecalculateResult,
  TeamMember,
  TenantSettings,
  TenantSettingsInput,
  UpdateMemberInput,
} from '@taim/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { meQueryKey } from '@/lib/auth';

export type TeamFilter = { active?: 'true' | 'false' | 'all'; search?: string };
export type UseTeamOptions = TeamFilter & { enabled?: boolean };

export const teamKeys = {
  all: ['team'] as const,
  list: (f: TeamFilter) => ['team', 'list', f.active ?? 'true', f.search ?? ''] as const,
  rates: ['rates'] as const,
  settings: ['settings'] as const,
};

/** Team members (GET /api/users). Members only ever receive active users. */
export function useTeam(opts: UseTeamOptions = {}) {
  return useQuery({
    queryKey: teamKeys.list(opts),
    queryFn: () =>
      api.get<TeamMember[]>('/api/users', { active: opts.active ?? 'true', search: opts.search?.trim() }),
    placeholderData: keepPreviousData,
    enabled: opts.enabled ?? true,
  });
}

/** Workspace settings (GET /api/settings); readable by everyone. */
export function useSettings() {
  return useQuery({
    queryKey: teamKeys.settings,
    queryFn: () => api.get<TenantSettings>('/api/settings'),
  });
}

export function useUpdateSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantSettingsInput) => api.patch<TenantSettings>('/api/settings', input),
    onSuccess: (settings) => {
      qc.setQueryData(teamKeys.settings, settings);
      // Workspace name/currency/week start also live on `me`.
      qc.invalidateQueries({ queryKey: meQueryKey });
    },
  });
}

export function useUpdateMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateMemberInput & { id: string }) =>
      api.patch<TeamMember>(`/api/users/${id}`, input),
    onSettled: () => qc.invalidateQueries({ queryKey: teamKeys.all }),
  });
}

export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteMemberInput) => api.post<TeamMember>('/api/users', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.all }),
  });
}

export function useRates(
  filter: { scope?: RateScope; userId?: string; projectId?: string } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: [...teamKeys.rates, filter],
    queryFn: () => api.get<Rate[]>('/api/rates', filter),
    enabled,
  });
}

/** YYYY-MM-DD for today in a timezone. */
export const todayIn = (timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(),
  );

/** YYYY-MM-DD `days` calendar days after a YYYY-MM-DD date. */
export function addDaysIso(date: string, days: number) {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** How far ahead a rate change reaches future-dated (planned) entries. */
const FUTURE_DAYS = 366;

/** Clockify's two ways to apply a changed rate. */
export type RateApplyMode = 'now' | 'all';
export const ALL_TIME = '1970-01-01';

export type ChangeRateArgs = {
  scope: 'workspace' | 'user';
  userId?: string;
  field: 'billRateCents' | 'costRateCents';
  cents: number | null;
  mode: RateApplyMode;
  /** Tenant timezone: decides what "now" (today) means. */
  timezone: string;
  /** Also re-rate entries imported from Clockify (they keep their Clockify rates otherwise). */
  includeImported?: boolean;
};

/**
 * Sets a workspace or user rate. "now" makes it effective today; "all" replaces the rate history
 * at that level. Either way existing entries in the affected range get their snapshot recalculated.
 *
 * `cents: null` removes the rate at that level for all dates (new entries fall back to the next
 * level) and leaves existing entries' snapshots alone, so nothing is recalculated.
 */
export function useChangeRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: ChangeRateArgs): Promise<{ removed: boolean; updated: number }> => {
      const target = { scope: a.scope, ...(a.userId ? { userId: a.userId } : {}) };
      if (a.cents === null) {
        const clear: RateInput = {
          ...target,
          [a.field]: null,
          effectiveFrom: ALL_TIME,
          replaceHistory: true,
        };
        await api.post('/api/rates', clear);
        return { removed: true, updated: 0 };
      }
      const today = todayIn(a.timezone);
      const from = a.mode === 'all' ? ALL_TIME : today;
      const input: RateInput = {
        ...target,
        [a.field]: a.cents,
        effectiveFrom: from,
        ...(a.mode === 'all' ? { replaceHistory: true } : {}),
      };
      await api.post<Rate>('/api/rates', input);
      // Future-dated entries already exist in the affected range, so they get the new rate too.
      const recalc: RecalculateInput = {
        from,
        to: addDaysIso(today, FUTURE_DAYS),
        ...(a.userId ? { userIds: [a.userId] } : {}),
        ...(a.includeImported ? { includeImported: true } : {}),
      };
      const result = await api.post<RecalculateResult>('/api/rates/recalculate', recalc);
      return { removed: false, updated: result.updated };
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: teamKeys.all });
      qc.invalidateQueries({ queryKey: teamKeys.rates });
    },
  });
}

/** Latest rate value in effect today among `rates` (non-null values only). */
export function currentRate(rates: Rate[] | undefined, field: ChangeRateArgs['field'], today: string) {
  const inEffect = (rates ?? [])
    .filter((r) => r.effectiveFrom <= today && r[field] !== null)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom));
  return inEffect[0]?.[field] ?? null;
}

/** "75", "75.5", "1,234.50" -> cents; "" -> null; invalid -> undefined. */
export function parseMoneyInput(text: string): number | null | undefined {
  const s = text.replace(/,/g, '').trim();
  if (s === '') return null;
  if (!/^\d+(\.\d{0,2})?$/.test(s)) return undefined;
  return Math.round(Number(s) * 100);
}

export const centsToInput = (cents: number | null | undefined) =>
  cents === null || cents === undefined ? '' : (cents / 100).toFixed(2);
